import { useState, useEffect } from 'react';
import { api } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { 
  TrendingUp, 
  TrendingDown, 
  Package, 
  ShoppingCart, 
  Users,
  AlertTriangle,
  Star,
  Warehouse,
  ClipboardList,
  FileText,
  Truck
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  AreaChart, 
  Area, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  Cell,
  PieChart,
  Pie
} from 'recharts';

const COLORS = ['#f97316', '#8b5cf6', '#10b981', '#3b82f6', '#ef4444', '#f59e0b', '#06b6d4', '#ec4899'];

export default function PurchaseAnalytics() {
  const [activeTab, setActiveTab] = useState('overview');
  const [purchaseData, setPurchaseData] = useState(null);
  const [vendorData, setVendorData] = useState(null);
  const [inventoryData, setInventoryData] = useState(null);
  const [categoryData, setCategoryData] = useState(null);
  const [assetData, setAssetData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAllAnalytics();
  }, []);

  const loadAllAnalytics = async () => {
    try {
      const [purchase, vendors, inventory, categories, assets] = await Promise.all([
        api.get('/analytics/purchase-summary'),
        api.get('/analytics/vendor-performance'),
        api.get('/analytics/inventory-metrics'),
        api.get('/analytics/category-spend'),
        api.get('/analytics/asset-summary')
      ]);
      
      setPurchaseData(purchase.data);
      setVendorData(vendors.data);
      setInventoryData(inventory.data);
      setCategoryData(categories.data);
      setAssetData(assets.data);
    } catch (error) {
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="analytics-loading">
        <div className="h-10 w-10 rounded-full border-4 border-orange-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="purchase-analytics">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white border shadow-sm">
          <TabsTrigger value="overview" data-testid="analytics-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="purchase" data-testid="analytics-tab-purchase">Purchase</TabsTrigger>
          <TabsTrigger value="inventory" data-testid="analytics-tab-inventory">Inventory</TabsTrigger>
          <TabsTrigger value="assets" data-testid="analytics-tab-assets">Assets</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6 mt-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Monthly Spend */}
            <Card className="bg-gradient-to-br from-orange-50 to-white border-orange-100" data-testid="kpi-monthly-spend">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1">This Month</p>
                    <p className="text-2xl font-bold text-gray-800" style={{ fontFamily: 'JetBrains Mono' }}>
                      {formatCurrency(purchaseData?.current_month_spend)}
                    </p>
                  </div>
                  <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                    purchaseData?.spend_change_percent >= 0 
                      ? 'bg-red-100 text-red-700' 
                      : 'bg-emerald-100 text-emerald-700'
                  }`}>
                    {purchaseData?.spend_change_percent >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                    {Math.abs(purchaseData?.spend_change_percent || 0)}%
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Stock Value */}
            <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-100" data-testid="kpi-stock-value">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1">Stock Value</p>
                    <p className="text-2xl font-bold text-gray-800" style={{ fontFamily: 'JetBrains Mono' }}>
                      {formatCurrency(inventoryData?.total_stock_value)}
                    </p>
                  </div>
                  <div className="h-10 w-10 rounded-xl bg-purple-100 flex items-center justify-center">
                    <Warehouse className="h-5 w-5 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Asset Value */}
            <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-100" data-testid="kpi-asset-value">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1">Asset Value</p>
                    <p className="text-2xl font-bold text-gray-800" style={{ fontFamily: 'JetBrains Mono' }}>
                      {formatCurrency(assetData?.total_current_value)}
                    </p>
                  </div>
                  <div className="h-10 w-10 rounded-xl bg-blue-100 flex items-center justify-center">
                    <Package className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Vendor Rating */}
            <Card className="bg-gradient-to-br from-emerald-50 to-white border-emerald-100" data-testid="kpi-vendor-rating">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1">Avg. Vendor Rating</p>
                    <div className="flex items-center gap-2">
                      <p className="text-2xl font-bold text-gray-800" style={{ fontFamily: 'JetBrains Mono' }}>
                        {vendorData?.average_rating || 0}
                      </p>
                      <Star className="h-5 w-5 text-amber-500 fill-amber-500" />
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">{vendorData?.total_vendors || 0}</p>
                    <p className="text-xs text-gray-400">vendors</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Alerts Row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-amber-200 bg-amber-50/50" data-testid="alert-low-stock">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-700" style={{ fontFamily: 'JetBrains Mono' }}>
                    {inventoryData?.low_stock_count || 0}
                  </p>
                  <p className="text-xs text-amber-600">Low Stock Items</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-blue-200 bg-blue-50/50" data-testid="alert-pending-prs">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <ClipboardList className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-700" style={{ fontFamily: 'JetBrains Mono' }}>
                    {purchaseData?.pending_prs || 0}
                  </p>
                  <p className="text-xs text-blue-600">Pending PRs</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-purple-200 bg-purple-50/50" data-testid="alert-pending-pos">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-700" style={{ fontFamily: 'JetBrains Mono' }}>
                    {purchaseData?.pending_pos || 0}
                  </p>
                  <p className="text-xs text-purple-600">Pending POs</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-emerald-200 bg-emerald-50/50" data-testid="alert-pending-grns">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Truck className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-emerald-700" style={{ fontFamily: 'JetBrains Mono' }}>
                    {purchaseData?.pending_grns || 0}
                  </p>
                  <p className="text-xs text-emerald-600">Pending GRNs</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Monthly Spend Trend */}
            <Card className="border-orange-100" data-testid="chart-monthly-spend">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5 text-orange-500" />
                  Monthly Spend Trend
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={purchaseData?.monthly_spend || []}>
                    <defs>
                      <linearGradient id="spendGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f97316" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#9ca3af" />
                    <YAxis 
                      tick={{ fontSize: 12 }} 
                      stroke="#9ca3af"
                      tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}k`}
                    />
                    <Tooltip 
                      formatter={(value) => [formatCurrency(value), 'Spend']}
                      labelFormatter={(label) => `Month: ${label}`}
                      contentStyle={{ borderRadius: 8, border: '1px solid #fed7aa' }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="spend" 
                      stroke="#f97316" 
                      strokeWidth={2}
                      fill="url(#spendGradient)" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Top Vendors */}
            <Card className="border-orange-100" data-testid="chart-top-vendors">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="h-5 w-5 text-purple-500" />
                  Top Vendors by Spend
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={vendorData?.top_vendors || []} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis 
                      type="number"
                      tick={{ fontSize: 12 }} 
                      stroke="#9ca3af"
                      tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}k`}
                    />
                    <YAxis 
                      type="category"
                      dataKey="vendor_name" 
                      tick={{ fontSize: 11 }} 
                      stroke="#9ca3af"
                      width={100}
                    />
                    <Tooltip 
                      formatter={(value) => [formatCurrency(value), 'Total Spend']}
                      contentStyle={{ borderRadius: 8, border: '1px solid #e9d5ff' }}
                    />
                    <Bar dataKey="total_amount" radius={[0, 4, 4, 0]}>
                      {(vendorData?.top_vendors || []).map((entry, index) => (
                        <Cell key={index} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Purchase Tab */}
        <TabsContent value="purchase" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Category Spend Breakdown */}
            <Card className="border-orange-100" data-testid="chart-category-spend">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Spend by Category</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={categoryData?.categories || []}
                      dataKey="amount"
                      nameKey="category"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      innerRadius={50}
                      paddingAngle={2}
                      label={({ category, percent }) => `${category} (${(percent * 100).toFixed(0)}%)`}
                      labelLine={false}
                    >
                      {(categoryData?.categories || []).map((entry, index) => (
                        <Cell key={index} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Vendor Performance Table */}
            <Card className="border-orange-100" data-testid="vendor-performance-table">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Vendor Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(vendorData?.top_vendors || []).map((vendor, index) => (
                    <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-orange-100 flex items-center justify-center text-sm font-bold text-orange-600">
                          {index + 1}
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">{vendor.vendor_name}</p>
                          <p className="text-xs text-gray-500">{vendor.total_orders} orders</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-800" style={{ fontFamily: 'JetBrains Mono' }}>
                          {formatCurrency(vendor.total_amount)}
                        </p>
                        <div className="flex items-center gap-1 justify-end">
                          <Star className="h-3 w-3 text-amber-500 fill-amber-500" />
                          <span className="text-xs text-gray-500">{vendor.rating || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                  {(!vendorData?.top_vendors || vendorData.top_vendors.length === 0) && (
                    <div className="text-center py-8 text-gray-500">
                      <Users className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                      <p>No vendor data available yet</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Inventory Tab */}
        <TabsContent value="inventory" className="space-y-6 mt-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-emerald-50 to-white border-emerald-100">
              <CardContent className="p-5">
                <p className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1">Stock In (30d)</p>
                <p className="text-2xl font-bold text-emerald-700" style={{ fontFamily: 'JetBrains Mono' }}>
                  {formatCurrency(inventoryData?.stock_in_30d)}
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-red-50 to-white border-red-100">
              <CardContent className="p-5">
                <p className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1">Stock Out (30d)</p>
                <p className="text-2xl font-bold text-red-700" style={{ fontFamily: 'JetBrains Mono' }}>
                  {formatCurrency(inventoryData?.stock_out_30d)}
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-100">
              <CardContent className="p-5">
                <p className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1">Turnover Rate</p>
                <p className="text-2xl font-bold text-blue-700" style={{ fontFamily: 'JetBrains Mono' }}>
                  {inventoryData?.turnover_rate || 0}x
                </p>
                <p className="text-xs text-gray-400">Annualized</p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-100">
              <CardContent className="p-5">
                <p className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1">Total Items</p>
                <p className="text-2xl font-bold text-purple-700" style={{ fontFamily: 'JetBrains Mono' }}>
                  {inventoryData?.total_stock_items || 0}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Store Breakdown */}
          <Card className="border-orange-100" data-testid="store-breakdown">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Warehouse className="h-5 w-5 text-purple-500" />
                Stock Value by Store
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {(inventoryData?.stores_breakdown || []).map((store, index) => (
                  <div key={index} className="p-4 rounded-lg bg-gray-50 border border-gray-100">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${COLORS[index % COLORS.length]}20` }}>
                        <Warehouse className="h-4 w-4" style={{ color: COLORS[index % COLORS.length] }} />
                      </div>
                      <p className="font-semibold text-gray-800">{store.store_name}</p>
                    </div>
                    <div className="flex items-end justify-between">
                      <div>
                        <p className="text-xs text-gray-500">{store.items} items</p>
                        <p className="text-xl font-bold text-gray-800" style={{ fontFamily: 'JetBrains Mono' }}>
                          {formatCurrency(store.value)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
                {(!inventoryData?.stores_breakdown || inventoryData.stores_breakdown.length === 0) && (
                  <div className="col-span-full text-center py-8 text-gray-500">
                    <Warehouse className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                    <p>No store data available yet</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Assets Tab */}
        <TabsContent value="assets" className="space-y-6 mt-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-100">
              <CardContent className="p-5">
                <p className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1">Total Assets</p>
                <p className="text-2xl font-bold text-blue-700" style={{ fontFamily: 'JetBrains Mono' }}>
                  {assetData?.total_assets || 0}
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-emerald-50 to-white border-emerald-100">
              <CardContent className="p-5">
                <p className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1">Purchase Value</p>
                <p className="text-2xl font-bold text-emerald-700" style={{ fontFamily: 'JetBrains Mono' }}>
                  {formatCurrency(assetData?.total_purchase_value)}
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-amber-50 to-white border-amber-100">
              <CardContent className="p-5">
                <p className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1">Depreciation</p>
                <p className="text-2xl font-bold text-amber-700" style={{ fontFamily: 'JetBrains Mono' }}>
                  {formatCurrency(assetData?.total_depreciation)}
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-red-50 to-white border-red-100">
              <CardContent className="p-5">
                <p className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1">Maintenance Due</p>
                <p className="text-2xl font-bold text-red-700" style={{ fontFamily: 'JetBrains Mono' }}>
                  {assetData?.upcoming_maintenance || 0}
                </p>
                <p className="text-xs text-gray-400">within 30 days</p>
              </CardContent>
            </Card>
          </div>

          {/* Asset Categories Breakdown */}
          <Card className="border-orange-100" data-testid="asset-categories-breakdown">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Package className="h-5 w-5 text-blue-500" />
                Asset Value by Category
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(assetData?.categories_breakdown || []).map((category, index) => {
                  const total = assetData?.total_current_value || 1;
                  const percentage = (category.value / total) * 100;
                  return (
                    <div key={index} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="h-3 w-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                          <span className="text-sm font-medium text-gray-700">{category.category_name}</span>
                          <Badge variant="outline" className="text-xs">{category.count} items</Badge>
                        </div>
                        <span className="text-sm font-semibold text-gray-800" style={{ fontFamily: 'JetBrains Mono' }}>
                          {formatCurrency(category.value)}
                        </span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all duration-500"
                          style={{ 
                            width: `${percentage}%`,
                            backgroundColor: COLORS[index % COLORS.length]
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
                {(!assetData?.categories_breakdown || assetData.categories_breakdown.length === 0) && (
                  <div className="text-center py-8 text-gray-500">
                    <Package className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                    <p>No asset data available yet</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
